import React, { useState, useEffect } from 'react';
import './App.css';

interface TextFile {
  filename: string;
  title: string;
  content: string;
  link: string | null;
}

// Text data will be loaded dynamically from the API

function App() {
  const [collectedString, setCollectedString] = useState<string>('');
  const [textData, setTextData] = useState<TextFile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [matchingContent, setMatchingContent] = useState<TextFile | null>(null);
  const [displayString, setDisplayString] = useState<string>('');
  const [disabledLetters, setDisabledLetters] = useState<Set<string>>(new Set());
  const [spacebarEnabled, setSpacebarEnabled] = useState<boolean>(false);
  const [letterStates, setLetterStates] = useState<{[key: string]: boolean} | null>(null);
  const [spacebarState, setSpacebarState] = useState<boolean | null>(null);

  // Guided mode: if exactly one match, predict the next character without calling search
  const [guidedActive, setGuidedActive] = useState<boolean>(false);
  const [guidedNextChar, setGuidedNextChar] = useState<string | null>(null);
  const [guidedMatch, setGuidedMatch] = useState<TextFile | null>(null);
  const [guidedForkString, setGuidedForkString] = useState<string | null>(null);

  // Local search helpers (fallback if /api/search fails)
  function normalizeText(text: string) {
    return (text || '')
      .toLowerCase()
      .replace(/[\-‒–—―]/g, ' ')
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getSearchableContent(file: TextFile): string {
    const original = file.content || '';
    const lines = original.split(/\r?\n/);
    return lines.slice(3).join('\n');
  }

  // Extract punctuation that follows the current word in the original content
  function getPunctuationAfterWord(file: TextFile, current: string): string {
    const original = getSearchableContent(file);
    const targetLen = normalizeText(current).length;
    if (targetLen === 0) return '';
    let normalizedLen = 0;
    let i = 0;
    let lastWasSpace = true;
    while (i < original.length && normalizedLen < targetLen) {
      const ch = original[i];
      if (/\w/.test(ch)) {
        normalizedLen += 1;
        lastWasSpace = false;
      } else if (/\s/.test(ch) || /[\-‒–—―]/.test(ch)) {
        if (!lastWasSpace) {
          normalizedLen += 1;
          lastWasSpace = true;
        }
      } else {
        // punctuation skipped in normalization
      }
      i += 1;
    }
    // i is original index right after the character that completed targetLen in normalized
    // Collect punctuation immediately following (before next letter or space)
    let j = i;
    let punct = '';
    while (j < original.length) {
      const ch = original[j];
      if (/\w/.test(ch) || /\s/.test(ch) || /[\-‒–—―]/.test(ch)) break;
      punct += ch;
      j += 1;
    }
    return punct;
  }

  function isWordBoundaryMatch(content: string, searchTerm: string) {
    if (!content || !searchTerm) return false;
    const normalizedContent = normalizeText(content);
    const normalizedSearch = normalizeText(searchTerm);
    const words = normalizedSearch.split(' ').filter(Boolean);
    if (words.length === 0) return false;
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = `\\b${esc(words[0])}` + words.slice(1).map(w => `\\s+\\b${esc(w)}`).join('');
    const regex = new RegExp(pattern, 'i');
    return regex.test(normalizedContent);
  }

  function searchFilesLocal(files: TextFile[], searchTerm: string) {
    if (!searchTerm || !searchTerm.trim()) return files;
    return files.filter(f => isWordBoundaryMatch(getSearchableContent(f), searchTerm));
  }

  function checkLetterMatchesLocal(files: TextFile[], currentString: string) {
    const alphabet = 'abcdefghijklmnopqrstuvwxyz';
    const out: Record<string, boolean> = {};
    for (const letter of alphabet) {
      const matches = searchFilesLocal(files, currentString + letter);
      out[letter] = matches.length > 0;
    }
    return out;
  }

  function checkSpacebarStateLocal(files: TextFile[], currentString: string) {
    if (currentString.trim() === '') return false;
    const withSpace = currentString + ' ';
    const spaceMatches = searchFilesLocal(files, withSpace);
    if (spaceMatches.length === 0) return false;
    const alphabet = 'abcdefghijklmnopqrstuvwxyz';
    for (const letter of alphabet) {
      if (searchFilesLocal(files, withSpace + letter).length > 0) return true;
    }
    return false;
  }

  // Compute next expected char from a single matched file's content (normalized substring match)
  function computeNextCharFromMatch(file: TextFile, current: string): string | null {
    const normalizedContent = normalizeText(getSearchableContent(file));
    const normalizedSearch = normalizeText(current);
    if (normalizedSearch === '') return null;
    const idx = normalizedContent.indexOf(normalizedSearch);
    if (idx === -1) return null;
    const nextIdx = idx + normalizedSearch.length;
    if (nextIdx >= normalizedContent.length) return null;
    const ch = normalizedContent.charAt(nextIdx);
    // Return space if next char is space, else the next letter
    if (ch === ' ') return ' ';
    if (ch >= 'a' && ch <= 'z') return ch;
    return null;
  }

  function computeNextCharOptions(file: TextFile, current: string): Set<string> {
    const normalizedContent = normalizeText(getSearchableContent(file));
    const normalizedSearch = normalizeText(current);
    const options = new Set<string>();
    if (normalizedSearch === '') return options;
    let fromIndex = 0;
    while (true) {
      const idx = normalizedContent.indexOf(normalizedSearch, fromIndex);
      if (idx === -1) break;
      const nextIdx = idx + normalizedSearch.length;
      if (nextIdx < normalizedContent.length) {
        const ch = normalizedContent.charAt(nextIdx);
        if (ch === ' ' || (ch >= 'a' && ch <= 'z')) {
          options.add(ch);
        }
      }
      fromIndex = idx + 1;
    }
    return options;
  }

  // Compute next non-space letter after at least one space has been consumed
  function computeNextAfterSpace(file: TextFile, current: string): string | null {
    const normalizedContent = normalizeText(getSearchableContent(file));
    const normalizedSearch = normalizeText(current);
    if (normalizedSearch === '') return null;
    const idx = normalizedContent.indexOf(normalizedSearch);
    if (idx === -1) return null;
    let nextIdx = idx + normalizedSearch.length;
    if (nextIdx >= normalizedContent.length) return null;
    // Skip one or more spaces to reach the next letter
    while (nextIdx < normalizedContent.length && normalizedContent.charAt(nextIdx) === ' ') {
      nextIdx++;
    }
    if (nextIdx >= normalizedContent.length) return null;
    const ch = normalizedContent.charAt(nextIdx);
    return (ch >= 'a' && ch <= 'z') ? ch : null;
  }


  // Load text data from API on component mount
  useEffect(() => {
    const loadTextData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/api/files');
        const data = await response.json();
        setTextData(data);
        // Initialize letter states locally so users can't start in the middle of a word before API returns
        const initLetterStates = checkLetterMatchesLocal(data, '');
        setLetterStates(initLetterStates);
        const initDisabled = new Set<string>();
        Object.entries(initLetterStates).forEach(([ltr, enabled]) => { if (!enabled) initDisabled.add(ltr); });
        setDisabledLetters(initDisabled);
        setSpacebarState(false);
        setSpacebarEnabled(false);
      } catch (error) {
        console.error('Error loading text data:', error);
        // Fallback to empty array if API fails
        setTextData([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadTextData();
  }, []);


  // Check if adding a letter would result in no matches
  const wouldLetterCauseNoMatches = (letter: string): boolean => {
    if (letter === '←') return false; // Backspace always allowed
    
    // Use the cached letter states
    if (letterStates && letterStates.hasOwnProperty(letter)) {
      return !letterStates[letter];
    }
    
    // Fallback if not cached: compute locally from textData to avoid starting mid-word
    const localStates = checkLetterMatchesLocal(textData, collectedString);
    if (localStates && localStates.hasOwnProperty(letter)) {
      return !localStates[letter];
    }
    return true;
  };

  // Check if spacebar should be shown
  const shouldShowSpacebar = (): boolean => {
    if (collectedString.trim() === '') return false; // No space at the beginning
    
    // Use the cached spacebar state
    if (spacebarState !== null) {
      return spacebarState;
    }
    
    // Fallback if not cached
    return false;
  };

  const addLetter = (letter: string, event?: React.MouseEvent) => {
    // Prevent keyboard event from firing when clicking
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    

    // In guided mode, allow only the guided next char (or backspace/space when expected) to avoid mid-word starts
    if (guidedActive) {
      if (letter !== '←') {
        if (letter === ' ') {
          if (guidedNextChar !== ' ') {
            
            return;
          }
        } else if (!guidedNextChar || letter !== guidedNextChar) {
          
          return;
        }
      }
    }

    if (letter === '←') {
      setCollectedString(prev => prev.slice(0, -1));
      setDisplayString(prev => prev.slice(0, -1));
      // On backspace, exit guided mode to re-evaluate possibilities
      
      setGuidedActive(false);
      setGuidedNextChar(null);
      setGuidedMatch(null);
      setGuidedForkString(null);
    } else if (letter === ' ') {
      const canAddSpace = (guidedActive && guidedNextChar === ' ') || shouldShowSpacebar();
      if (canAddSpace) {
        setCollectedString(prev => {
          const newStr = prev + ' ';
          if (guidedActive && guidedMatch) {
            // Insert punctuation from the source after the completed word
            const punct = getPunctuationAfterWord(guidedMatch, prev);
            if (punct) {
              setDisplayString(dPrev => dPrev + punct + ' ');
            } else {
              setDisplayString(dPrev => dPrev + ' ');
            }

            const nextChar = computeNextAfterSpace(guidedMatch, newStr);
            if (nextChar) {
              setGuidedNextChar(nextChar);
              const alphabet = 'abcdefghijklmnopqrstuvwxyz';
              const ls: {[key: string]: boolean} = {};
              for (const l of alphabet) ls[l] = nextChar === l;
              setLetterStates(ls);
              setDisabledLetters(new Set(alphabet.split('').filter(l => l !== nextChar)));
              // After consuming space, we only enable the next letter
              setSpacebarState(false);
              setSpacebarEnabled(false);
            } else {
              
              setGuidedActive(false);
              setGuidedNextChar(null);
              setGuidedMatch(null);
              setGuidedForkString(null);
              setSpacebarState(false);
              setSpacebarEnabled(false);
            }
          } else {
            // Non-guided: use single matching content if present to inject punctuation
            if (matchingContent) {
              const punct = getPunctuationAfterWord(matchingContent, prev);
              if (punct) {
                setDisplayString(dPrev => dPrev + punct + ' ');
              } else {
                setDisplayString(dPrev => dPrev + ' ');
              }
            } else {
              setDisplayString(dPrev => dPrev + ' ');
            }
            setSpacebarEnabled(false);
            setSpacebarState(false);
          }
          return newStr;
        });
      }
    } else {
      // Only add letter if it won't cause no matches
      if (!wouldLetterCauseNoMatches(letter)) {
        setCollectedString(prev => {
          const newStr = prev + letter;
          setDisplayString(dPrev => dPrev + letter);
          if (guidedActive && guidedMatch) {
            if (guidedNextChar && letter === guidedNextChar) {
              const nextChar = computeNextCharFromMatch(guidedMatch, newStr);
              setGuidedNextChar(nextChar);
            } else {
              
              setGuidedActive(false);
              setGuidedNextChar(null);
              setGuidedMatch(null);
              setGuidedForkString(null);
            }
          }
          return newStr;
        });
      }
    }
  };

  // Keyboard input disabled to prevent glitching
  // const handleKeyPress = (event: React.KeyboardEvent) => {
  //   const key = event.key.toLowerCase();
  //   
  //   if (key === 'backspace') {
  //     addLetter('←');
  //     return;
  //   }

  //   if (key === ' ') {
  //     if (shouldShowSpacebar()) {
  //       addLetter(' ');
  //     }
  //     return;
  //   }

  //   // Check if it's a letter and would cause no matches
  //   if (key >= 'a' && key <= 'z') {
  //     if (!wouldLetterCauseNoMatches(key)) {
  //       addLetter(key);
  //     }
  //   }
  // };

  // Global keyboard input disabled to prevent glitching
  // useEffect(() => {
  //   const handleGlobalKeyPress = (event: KeyboardEvent) => {
  //     const key = event.key.toLowerCase();
  //     
  //     if (key === 'backspace') {
  //       addLetter('←');
  //       return;
  //     }

  //     if (key === ' ') {
  //       if (shouldShowSpacebar()) {
  //         addLetter(' ');
  //       }
  //       return;
  //     }

  //     // Check if it's a letter and would cause no matches
  //     if (key >= 'a' && key <= 'z') {
  //       if (!wouldLetterCauseNoMatches(key)) {
  //         addLetter(key);
  //       }
  //     }
  //   };

  //   document.addEventListener('keydown', handleGlobalKeyPress);
  //   return () => document.removeEventListener('keydown', handleGlobalKeyPress);
  // }, [collectedString]); // Add collectedString as dependency

  // Single comprehensive search when collected string changes
  useEffect(() => {
    if (isLoading) return;

    // If guided mode is active, don't call the API. Just reflect the single allowed next char.
    if (guidedActive && guidedMatch) {
      
      // Show the current collected string as link if exactly one match
      setMatchingContent(guidedMatch);
      // Only enable the guided next char; enable spacebar when guidedNextChar is space
      const alphabet = 'abcdefghijklmnopqrstuvwxyz';
      const ls: {[key: string]: boolean} = {};
      for (const l of alphabet) ls[l] = guidedNextChar ? (guidedNextChar === l) : false;
      setLetterStates(ls);
      setDisabledLetters(new Set(alphabet.split('').filter(l => !guidedNextChar || l !== guidedNextChar)));
      const spaceOk = guidedNextChar === ' ';
      setSpacebarState(spaceOk);
      setSpacebarEnabled(spaceOk);
      if (!guidedNextChar) {
        // End of match reached; exit guided and allow normal search to resume next cycle
        
        setGuidedActive(false);
        setGuidedMatch(null);
      }
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`/api/search?q=${encodeURIComponent(collectedString)}`, {
          signal: controller.signal,
        });
        const contentType = response.headers.get('content-type') || '';
        if (!response.ok || contentType.includes('text/html')) {
          throw new Error(`Search unavailable (${response.status})`);
        }
        const data = await response.json();

        // Update matching content
        setMatchingContent(data.matches.length === 1 ? data.matches[0] : null);

        // Update letter states
        setLetterStates(data.letterStates);
        const newDisabledLetters = new Set<string>();
        Object.entries(data.letterStates as Record<string, boolean>).forEach(([letter, enabled]) => {
          if (!enabled) newDisabledLetters.add(letter);
        });
        setDisabledLetters(newDisabledLetters);

        // Update spacebar state
        setSpacebarState(data.spacebarEnabled);
        setSpacebarEnabled(data.spacebarEnabled);

        // Enter guided mode only if exactly one match AND there is a unique next character across all occurrences
        if (data.matches && data.matches.length === 1) {
          const match: TextFile = data.matches[0];
          const options = computeNextCharOptions(match, collectedString);
          const unique = options.size === 1 ? Array.from(options)[0] : null;
          
          if (unique) {
            setGuidedActive(true);
            setGuidedNextChar(unique);
            setGuidedMatch(match);
            setGuidedForkString(collectedString);
          } else {
            setGuidedActive(false);
            setGuidedNextChar(null);
            setGuidedMatch(null);
            setGuidedForkString(null);
          }
        } else {
          setGuidedActive(false);
          setGuidedNextChar(null);
          setGuidedMatch(null);
          setGuidedForkString(null);
        }
      } catch (error) {
        // Fallback: compute locally from already-loaded textData
        
        const matches = searchFilesLocal(textData, collectedString);
        const letterStatesLocal = checkLetterMatchesLocal(textData, collectedString);
        const spacebarEnabledLocal = checkSpacebarStateLocal(textData, collectedString);

        setMatchingContent(matches.length === 1 ? matches[0] : null);
        setLetterStates(letterStatesLocal);
        const newDisabledLetters = new Set<string>();
        Object.entries(letterStatesLocal).forEach(([letter, enabled]) => {
          if (!enabled) newDisabledLetters.add(letter);
        });
        setDisabledLetters(newDisabledLetters);
        setSpacebarState(spacebarEnabledLocal);
        setSpacebarEnabled(spacebarEnabledLocal);

        // Enter guided mode on single local match only if unique next character
        if (matches.length === 1) {
          const match: TextFile = matches[0];
          const options = computeNextCharOptions(match, collectedString);
          const unique = options.size === 1 ? Array.from(options)[0] : null;
          
          if (unique) {
            setGuidedActive(true);
            setGuidedNextChar(unique);
            setGuidedMatch(match);
            setGuidedForkString(collectedString);
          } else {
            setGuidedActive(false);
            setGuidedNextChar(null);
            setGuidedMatch(null);
            setGuidedForkString(null);
          }
        } else {
          setGuidedActive(false);
          setGuidedNextChar(null);
          setGuidedMatch(null);
          setGuidedForkString(null);
        }
      }
    }, 200); // debounce to reduce server load

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [collectedString, isLoading, textData]);

  if (isLoading) {
    return (
      <div className="App">
        <div className="content-container">
          <div className="content-section">
            <div className="section-title">Loading texts...</div>
          </div>
        </div>
        <div className="alphabet-container">
          <div className="alphabet-row">
            {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i'].map(letter => (
              <div 
                key={letter} 
                className="letter disabled"
              >
                {letter}
              </div>
            ))}
          </div>
          <div className="alphabet-row">
            {['j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r'].map(letter => (
              <div 
                key={letter} 
                className="letter disabled"
              >
                {letter}
              </div>
            ))}
          </div>
          <div className="alphabet-row">
            {['s', 't', 'u', 'v', 'w', 'x', 'y', 'z', '←'].map(letter => (
              <div 
                key={letter} 
                className="letter disabled"
              >
                {letter}
              </div>
            ))}
          </div>
          <div className="alphabet-row space-row">
            <div className="letter space-bar disabled">
              space
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      
      <div className="content-container">
        <div className="content-section">
          <div className="content-item">
            {matchingContent ? (
              <a 
                href={matchingContent.link || '#'} 
                className="content-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {displayString}
              </a>
            ) : (
              displayString || ''
            )}
          </div>
        </div>
      </div>

      <div className="alphabet-container">
        <div className="alphabet-row">
          {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i'].map(letter => (
            <div 
              key={letter} 
              className={`letter ${disabledLetters.has(letter) ? 'disabled' : ''}`}
              onClick={(e) => addLetter(letter, e)}
            >
              {letter}
            </div>
          ))}
        </div>
        <div className="alphabet-row">
          {['j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r'].map(letter => (
            <div 
              key={letter} 
              className={`letter ${disabledLetters.has(letter) ? 'disabled' : ''}`}
              onClick={(e) => addLetter(letter, e)}
            >
              {letter}
            </div>
          ))}
        </div>
        <div className="alphabet-row">
          {['s', 't', 'u', 'v', 'w', 'x', 'y', 'z', '←'].map(letter => (
            <div 
              key={letter} 
              className={`letter ${disabledLetters.has(letter) ? 'disabled' : ''}`}
              onClick={(e) => addLetter(letter, e)}
            >
              {letter}
            </div>
          ))}
        </div>
        <div className="alphabet-row space-row">
          <div 
            className={`letter space-bar ${spacebarEnabled ? '' : 'disabled'}`}
            onClick={(e) => spacebarEnabled && addLetter(' ', e)}
          >
            space
          </div>
        </div>
      </div>
      
      {/* Information button */}
      <div 
        className="info-button" 
        onClick={() => alert('email will@willallstetter.com to submit')}
      >
        ⓘ
      </div>
    </div>
  );
}

export default App;